import { Suspense, lazy, useMemo } from 'react';

import { ErrorBoundary } from '@/components/ErrorBoundary';
import { useTheme } from '@/components/ThemeProvider';
import { Skeleton } from '@/components/ui/skeleton';

import { useFetch } from 'hooks/useFetch';

import { type IPlotlyElement } from 'client-types/';

const Plot = lazy(() => import('react-plotly.js'));

// Backend ships layout colors tuned for light mode. In dark mode we walk the
// layout once after fetch and swap the text/axis colors for values that read
// on the dark navy background (#d1d5db text, #9ca3af zero-lines, etc.).
// Plotly renders to inline SVG so CSS cascading can't reach the text — this
// in-JS override is the only way to retheme a chart cross-mode.
const applyDarkThemeOverrides = (layout: any): any => {
  if (!layout) return layout;
  const darkText = '#d1d5db';
  const darkTitle = '#f3f4f6';
  const darkGrid = '#1f2a3a';
  const darkZero = '#9ca3af';
  const darkAxisLine = '#4b5563';
  const darkTick = '#6b7280';

  layout.font = { ...(layout.font || {}), color: darkText };
  if (layout.title) {
    const titleObj =
      typeof layout.title === 'string' ? { text: layout.title } : layout.title;
    layout.title = {
      ...titleObj,
      font: { ...(titleObj.font || {}), color: darkTitle }
    };
  }
  if (layout.legend) {
    layout.legend = {
      ...layout.legend,
      font: { ...(layout.legend.font || {}), color: darkText }
    };
  }
  // Swap the leading palette entry (backend ships the light-mode primary,
  // which is too dark to read on a navy background) for the theme.json
  // dark-mode primary = hsl(210, 91%, 56%).
  if (Array.isArray(layout.colorway) && layout.colorway.length) {
    layout.colorway = ['#298ff5', ...layout.colorway.slice(1)];
  }
  for (const axisKey of ['xaxis', 'yaxis']) {
    const axis = layout[axisKey];
    if (!axis) continue;
    layout[axisKey] = {
      ...axis,
      color: darkText,
      linecolor: darkAxisLine,
      gridcolor: darkGrid,
      zerolinecolor: darkZero,
      tickcolor: darkTick,
      title: axis.title
        ? {
            ...(typeof axis.title === 'string'
              ? { text: axis.title }
              : axis.title),
            font: {
              ...((typeof axis.title === 'object' && axis.title?.font) || {}),
              color: darkText
            }
          }
        : axis.title
    };
  }
  // Polar layout: applies to scatterpolar / barpolar (rose curves, cardioids,
  // wind roses). Same palette as Cartesian — angle ticks and radial gridlines
  // need to read on the navy background, otherwise they vanish.
  if (layout.polar) {
    const polar = layout.polar;
    const polarAxisOverride = (axis: any) =>
      axis
        ? {
            ...axis,
            color: darkText,
            linecolor: darkAxisLine,
            gridcolor: darkGrid,
            tickcolor: darkTick,
            tickfont: {
              ...(axis.tickfont || {}),
              color: darkText
            }
          }
        : axis;
    layout.polar = {
      ...polar,
      bgcolor: 'rgba(0,0,0,0)',
      angularaxis: polarAxisOverride(polar.angularaxis),
      radialaxis: polarAxisOverride(polar.radialaxis)
    };
  }
  return layout;
};

interface Props {
  element: IPlotlyElement;
}

const _PlotlyElement = ({ element }: Props) => {
  const { data, error, isLoading } = useFetch(element.url || null);
  const { variant } = useTheme();

  // deep-clone SWR data so Plotly.js mutations don't corrupt the cache.
  // keyed on the data reference + theme variant so dark/light swaps
  // remount with fresh colors rather than holding cached values.
  const plotly = useMemo(() => {
    if (!data) return null;
    const layout = structuredClone(data.layout);
    if (variant === 'dark') {
      applyDarkThemeOverrides(layout);
    }
    return {
      data: structuredClone(data.data),
      layout,
      frames: data.frames ? structuredClone(data.frames) : undefined,
      config: data.config ? structuredClone(data.config) : undefined,
      height: data.layout?.height || 400
    };
  }, [data, variant]);

  if (isLoading) return <div>Loading...</div>;
  if (error) return <div>An error occurred</div>;
  if (!plotly) return null;

  return (
    <Suspense fallback={<Skeleton className="h-full rounded-md" />}>
      <div style={{ width: '100%', height: `${plotly.height}px` }}>
        <Plot
          className={`${element.display}-plotly`}
          data={plotly.data}
          layout={plotly.layout}
          frames={plotly.frames}
          config={plotly.config}
          style={{
            width: '100%',
            height: '100%',
            borderRadius: '1rem',
            overflow: 'hidden'
          }}
          useResizeHandler={true}
        />
      </div>
    </Suspense>
  );
};

const PlotlyElement = (props: Props) => {
  return (
    <ErrorBoundary prefix="Failed to load chart.">
      <_PlotlyElement {...props} />
    </ErrorBoundary>
  );
};

export { PlotlyElement };
